import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Stack } from "@mui/material";
import { useNavigate } from "react-router";
import Topbar from "../../../../../component/TopBar";
import MainBox from "../../../container/MainBox";
import DataTable from "../../../../../component/DataTable";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import { StatusChip, PersonCell, SearchInput, FilterSelect } from "../../../../../component/listing";
import { ensureOrgEmployees, ensureOrgDepartments } from "../../../../../services/useOrgData";
import { getAllTimesheets, getTeamTimesheets, getTimesheetSettings } from "../../../../../services/timesheet";
import { useNotify } from "../../../../../component/NotificationProvider";
import { tokens, displayFont, chartColors, motion } from "../../../../../theme";
import { pressable } from "../../../../../component/pressableProps";
import { counterRowSx } from "../../../container/frameLayout";
import {
  weekStart,
  weekLabel,
  weeksIn,
  inWeek,
  hours,
  hoursLabel,
  summarise,
  projectShare,
  buildRows,
  rowStatus,
  selectRows,
  MISSING_COUNTERS,
} from "./timesheetWeek";

/**
 * A counter, to the book's spec: a small-caps label, an Archivo figure, and one
 * line of context under it.
 *
 * `tone` colours the figure, and only the counters a manager has to act on take
 * one — unapproved sheets and overtime to sign off. Everything else is ink, so
 * the ones that need a decision are the ones the eye finds.
 */
const TONE_INK = {
  danger: tokens.danger,
  warning: tokens.warningText,
  success: tokens.successText,
};

const Counter = ({ label, value, sub, tone, onClick, active }) => (
  <Box
    {...(onClick ? pressable(onClick) : {})}
    sx={{
      background: tokens.card,
      border: `1px solid ${active ? tokens.primary : tokens.border}`,
      boxShadow: active ? `0 0 0 1px ${tokens.primary}` : "none",
      borderRadius: "10px",
      p: "1rem 1.125rem",
      cursor: onClick ? "pointer" : "default",
      transition: `border-color ${motion.duration[1]}ms ${motion.easing.standard}`,
      "&:hover": onClick ? { borderColor: tokens.primary } : {},
    }}
  >
    <Box
      sx={{
        fontSize: "0.75rem",
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: tokens.textMuted,
        mb: "0.625rem",
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontFamily: displayFont,
        fontSize: "1.625rem",
        fontWeight: 700,
        lineHeight: 1.1,
        fontVariantNumeric: "tabular-nums",
        color: TONE_INK[tone] || tokens.text,
      }}
    >
      {value}
    </Box>
    {sub != null && (
      <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, mt: "0.25rem" }}>{sub}</Box>
    )}
  </Box>
);

const Panel = ({ title, caption, right, children }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: "20rem",
      background: tokens.card,
      border: `1px solid ${tokens.border}`,
      borderRadius: "10px",
      p: "1.375rem",
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: "0.875rem" }}>
      <Box>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.0625rem", fontWeight: 700, color: tokens.text }}>
          {title}
        </Box>
        {caption && (
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.1875rem" }}>{caption}</Box>
        )}
      </Box>
      {right}
    </Stack>
    {children}
  </Box>
);

/**
 * Where the hours went.
 *
 * One row per project, each with its own track, and the track is scaled against
 * the largest project rather than the total — the percentage column already
 * answers "how much of the week", so the bar answers "how do these compare".
 * Projects keep their palette colour, the same rule as Reporting and Expenses.
 */
const PROJECT_GRID = "1.5fr 1fr 5.25rem 3.375rem";

const ProjectBreakdown = ({ share }) => {
  const peak = Math.max(1, ...share.rows.map((p) => p.hours));
  return (
    <Box>
      {share.rows.map((p) => {
        const colour = chartColors[p.colorIndex % chartColors.length];
        return (
          <Box
            key={p.name}
            sx={{
              display: "grid",
              gridTemplateColumns: PROJECT_GRID,
              gap: "0.875rem",
              alignItems: "center",
              py: "0.6875rem",
              borderBottom: `1px solid ${tokens.border}`,
              fontSize: "0.875rem",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.125} sx={{ minWidth: 0 }}>
              <Box sx={{ width: "0.625rem", height: "0.625rem", borderRadius: "3px", background: colour, flex: "none" }} />
              <Box sx={{ color: tokens.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.name}
              </Box>
            </Stack>
            <Box sx={{ height: "0.5rem", borderRadius: "4px", background: tokens.border, overflow: "hidden" }}>
              <Box
                sx={{
                  width: `${(p.hours / peak) * 100}%`,
                  height: "100%",
                  background: colour,
                  transition: `width ${motion.duration[3]}ms ${motion.easing.decelerate}`,
                }}
              />
            </Box>
            <Box sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: tokens.text }}>
              {hoursLabel(p.hours)}
            </Box>
            <Box sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: tokens.textMuted, fontSize: "0.8125rem" }}>
              {(p.share * 100).toFixed(1)}%
            </Box>
          </Box>
        );
      })}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: PROJECT_GRID,
          gap: "0.875rem",
          alignItems: "center",
          pt: "0.75rem",
          fontSize: "0.875rem",
          fontWeight: 700,
        }}
      >
        <Box sx={{ color: tokens.text }}>Total</Box>
        <Box />
        <Box sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: tokens.text }}>
          {hoursLabel(share.total)}
        </Box>
        <Box sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: "0.8125rem", color: tokens.text }}>
          100%
        </Box>
      </Box>
    </Box>
  );
};

const Timesheet = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [sheets, setSheets] = useState([]);
  const [roster, setRoster] = useState([]);
  // Only to turn the department id on an employee into a team name — the
  // field is a String id, so it cannot name itself.
  const [departments, setDepartments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [week, setWeek] = useState("");
  const [bucket, setBucket] = useState("awaiting");
  const [search, setSearch] = useState("");
  // True when the org-wide call was refused and this is one manager's team.
  const [teamScoped, setTeamScoped] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setFailed(false);
      try {
        /**
         * Who is reading this screen decides which endpoint answers it.
         *
         * The book calls this "the manager's week", but `GET /timesheet` is
         * admin-only — so a reporting manager, the exact reader it is written
         * for, got a 403 and an error state. `/timesheet/team` returns the
         * caller's direct reports and needs no admin right, so a 403 on the
         * org-wide call is not a failure: it is the answer that this reader is
         * a manager rather than an admin.
         *
         * Only a 403 falls through. Any other failure is a real one and is
         * reported, rather than being quietly retried against a narrower list
         * that would understate the week.
         */
        const [orgRes, employees, setRes, depts] = await Promise.all([
          getAllTimesheets(),
          ensureOrgEmployees().catch(() => []),
          // Settings carry the overtime threshold; losing them is not a failure
          // of the screen, so it falls back rather than throwing.
          getTimesheetSettings().catch(() => null),
          // Names for the Team column. Same treatment: losing them costs a
          // dash, not the screen.
          ensureOrgDepartments().catch(() => []),
        ]);
        if (!active) return;
        setDepartments(Array.isArray(depts) ? depts : []);

        let tsRes = orgRes;
        let scopedToTeam = false;
        if (orgRes?.success === false && orgRes?.status === 403) {
          tsRes = await getTeamTimesheets();
          scopedToTeam = true;
        }
        if (!active) return;
        if (tsRes?.success === false) {
          throw new Error(tsRes?.message || "Failed to load timesheets");
        }

        const list = Array.isArray(tsRes?.data) ? tsRes.data : [];
        setSheets(list);
        setTeamScoped(scopedToTeam);

        /**
         * The denominator has to match the list.
         *
         * "118 of 142" is a claim about who was asked. For an admin that is the
         * whole active roster; for a manager it is their own reports, and
         * counting the org would report every other team as outstanding on this
         * manager's screen. Reports are derived from the sheets returned, which
         * is the only definition of "my team" this screen can see.
         */
        const all = employees || [];
        if (scopedToTeam) {
          const mine = new Set(list.map((t) => String(t.employee?._id || t.employee || "")));
          setRoster(all.filter((e) => mine.has(String(e._id))));
        } else {
          setRoster(all);
        }

        setSettings(setRes?.data || null);
        setWeek((current) => current || weeksIn(list)[0] || weekStart(new Date().toISOString()));
      } catch (err) {
        if (active) {
          setFailed(true);
          notify.error(err?.message || "Failed to load timesheets");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const weekSheets = useMemo(() => sheets.filter((s) => inWeek(s, week)), [sheets, week]);
  const stats = useMemo(
    () => summarise(weekSheets, {
      headcount: roster.length,
      weeklyRegularHours: settings?.weeklyRegularHours || 40,
    }),
    [weekSheets, roster, settings]
  );
  const share = useMemo(() => projectShare(weekSheets), [weekSheets]);
  const rows = useMemo(
    () => buildRows(sheets, roster, week, departments),
    [sheets, roster, week, departments]
  );
  const visible = useMemo(() => selectRows(rows, bucket, search), [rows, bucket, search]);

  const weekOptions = useMemo(() => {
    const known = weeksIn(sheets);
    if (week && !known.includes(week)) known.unshift(week);
    return known.map((k) => ({ value: k, label: weekLabel(k) }));
  }, [sheets, week]);

  const openSheet = useCallback((row) => {
    if (row.sheetId) navigate(`/app/timesheet/${row.sheetId}`);
  }, [navigate]);

  const columns = [
    {
      key: "employee",
      label: "Employee",
      width: "26%",
      render: (r) => <PersonCell name={r.employeeName} src={r.employee?.profileImage} />,
    },
    {
      key: "team",
      label: "Team",
      cellSx: { color: tokens.textMuted },
      render: (r) => r.team,
    },
    {
      key: "hours",
      label: "Hours",
      align: "right",
      width: "6rem",
      cellSx: { fontVariantNumeric: "tabular-nums" },
      // A person with no sheet has no hours at all. Rendering 0.0 would read as
      // a submitted week in which nobody worked.
      render: (r) => (r.totalHours == null ? "—" : hours(r.totalHours)),
    },
    {
      key: "overtime",
      label: "Overtime",
      align: "right",
      width: "6rem",
      render: (r) =>
        r.overtimeHours ? (
          <Box component="span" sx={{ color: tokens.warningText, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            {hours(r.overtimeHours)}
          </Box>
        ) : (
          <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>—</Box>
        ),
    },
    {
      key: "status",
      label: "Status",
      width: "9rem",
      render: (r) => {
        const s = rowStatus(r);
        return <StatusChip label={s.label} tone={s.tone} />;
      },
    },
    {
      key: "action",
      label: "",
      width: "11rem",
      cellSx: { py: "0.5rem" },
      render: (r) => {
        const s = rowStatus(r);
        if (s.action === "approve") {
          return (
            <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
              <BtnPrimary onClick={() => openSheet(r)} sx={{ borderRadius: "6px", px: "0.875rem", py: "0.4375rem", fontSize: "0.8125rem", minWidth: 0 }}>
                Approve
              </BtnPrimary>
              <BtnSecondary onClick={() => openSheet(r)} sx={{ borderRadius: "6px", px: "0.75rem", py: "0.4375rem", fontSize: "0.8125rem", minWidth: 0 }}>
                Open
              </BtnSecondary>
            </Stack>
          );
        }
        if (s.action === "view") {
          return (
            <Stack direction="row" onClick={(e) => e.stopPropagation()}>
              <BtnSecondary onClick={() => openSheet(r)} sx={{ borderRadius: "6px", px: "0.75rem", py: "0.4375rem", fontSize: "0.8125rem", minWidth: 0 }}>
                View
              </BtnSecondary>
            </Stack>
          );
        }
        // The book offers "Remind" here. There is no reminder endpoint, and a
        // button that does nothing is worse than an absent one, so the row says
        // what is true instead.
        return (
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textPlaceholder }}>
            {s.action === "remind" ? "Nothing submitted" : "In progress"}
          </Box>
        );
      },
    },
  ];

  const CHIPS = [
    { key: "awaiting", label: "Awaiting you", n: stats.awaiting.count },
    { key: "outstanding", label: "Outstanding", n: stats.submitted.outstanding },
    { key: "overtime", label: "Overtime", n: stats.overtime.people },
    { key: "approved", label: "Approved", n: rows.filter((r) => r.status === "approved").length },
    { key: "all", label: "Everyone", n: rows.length },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Topbar
        MainHeading={"Timesheets"}
        breadcrumbs={[{ label: "Timesheet", href: "" }, { label: "Dashboard", href: "" }]}
      />
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ md: "flex-start" }}
          spacing={1.5}
          sx={{ mb: "1.25rem" }}
        >
          <Box>
            <Box sx={{ fontFamily: displayFont, fontSize: "1.5rem", fontWeight: 700, color: tokens.text }}>
              {weekLabel(week) || "Timesheets"}
            </Box>
            <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, mt: "0.25rem" }}>
              {loading
                ? " "
                : [
                    // Say whose week this is. A manager reading "9 people" needs
                    // to know that is their team and not the company.
                    teamScoped
                      ? `your team · ${roster.length} ${roster.length === 1 ? "person" : "people"}`
                      : `${roster.length} ${roster.length === 1 ? "person" : "people"}`,
                    `overtime above ${settings?.weeklyRegularHours || 40}h`,
                    stats.awaiting.count ? `${stats.awaiting.count} awaiting approval` : null,
                  ].filter(Boolean).join(" · ")}
            </Box>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {weekOptions.length > 1 && (
              <FilterSelect
                value={week}
                onChange={(e) => setWeek(e.target.value)}
                options={weekOptions}
                inputProps={{ "aria-label": "Week" }}
              />
            )}
            <BtnPrimary to="/app/timesheet/new" sx={{ borderRadius: "8px" }}>New timesheet</BtnPrimary>
          </Stack>
        </Stack>

        {/* Five counters. The book has six; the sixth is "Submitted late", which
            needs a submission timestamp the model does not carry. */}
        <Box
          sx={{
            ...counterRowSx(5),
            mb: "1.25rem",
          }}
        >
          <Counter
            label="Submitted"
            value={stats.submitted.count}
            sub={`of ${stats.submitted.of} · ${stats.submitted.outstanding} outstanding`}
            onClick={() => setBucket("all")}
            active={bucket === "all"}
          />
          <Counter
            label="Awaiting you"
            value={stats.awaiting.count}
            sub={`${stats.awaiting.decided} already decided`}
            tone={stats.awaiting.count > 0 ? "warning" : undefined}
            onClick={() => setBucket("awaiting")}
            active={bucket === "awaiting"}
          />
          <Counter
            label="Hours logged"
            value={hoursLabel(stats.logged.hours)}
            sub={`across ${stats.logged.sheets} timesheet${stats.logged.sheets === 1 ? "" : "s"}`}
          />
          <Counter
            label="Overtime"
            value={hoursLabel(stats.overtime.hours)}
            sub={`${stats.overtime.people} ${stats.overtime.people === 1 ? "person" : "people"} above ${stats.overtime.threshold}h`}
            tone={stats.overtime.hours > 0 ? "warning" : undefined}
            onClick={() => setBucket("overtime")}
            active={bucket === "overtime"}
          />
          <Counter
            label="Billable"
            value={stats.billable.share == null ? "—" : `${Math.round(stats.billable.share * 100)}%`}
            sub={
              stats.billable.share == null
                ? "nothing logged yet"
                : `${Math.round(stats.billable.hours).toLocaleString("en-IN")} of ${Math.round(stats.billable.of).toLocaleString("en-IN")} hours`
            }
          />
        </Box>

        <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} sx={{ mb: "1.25rem" }}>
          <Panel
            title="Where the hours went"
            caption={`${weekLabel(week)}, all departments`}
          >
            {share.rows.length ? (
              <ProjectBreakdown share={share} />
            ) : (
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
                No hours have been submitted for this week yet.
              </Box>
            )}
          </Panel>

          {/* The book's second panel is "When they submitted", a chart of
              submissions by day against the Friday deadline. Nothing records
              when a timesheet was submitted, so the panel states what is
              missing rather than charting a proxy that would be wrong. */}
          <Panel title="When they submitted" caption="Not available yet">
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, lineHeight: 1.6 }}>
              A timesheet does not record when it was submitted, so submissions
              cannot be plotted against the deadline and{" "}
              {MISSING_COUNTERS.map((c) => `"${c.label}"`).join(" and ")} cannot be
              counted. The last edit time moves again when a manager approves, so
              it would report approvals as late submissions.
            </Box>
          </Panel>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ md: "center" }}
          spacing={2}
          sx={{ mb: "1rem" }}
        >
          <Box sx={{ fontFamily: displayFont, fontSize: "1.0625rem", fontWeight: 700, color: tokens.text }}>
            Waiting on you
          </Box>
          <Box sx={{ flex: 1, height: "1px", background: tokens.border, display: { xs: "none", md: "block" } }} />
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search employee"
          />
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: "1rem", flexWrap: "wrap", rowGap: 1 }}>
          {CHIPS.map((c) => {
            const active = bucket === c.key;
            return (
              <Box
                key={c.key}
                {...pressable(() => setBucket(c.key))}
                sx={{
                  fontSize: "0.8125rem",
                  fontWeight: active ? 600 : 400,
                  cursor: "pointer",
                  px: "0.875rem",
                  py: "0.5rem",
                  borderRadius: "8px",
                  color: active ? tokens.card : tokens.textMuted,
                  background: active ? tokens.primary : "transparent",
                  border: `1px solid ${active ? tokens.primary : tokens.borderStrong}`,
                  "&:hover": { background: active ? tokens.primary : tokens.surfaceSubtle },
                }}
              >
                {c.label} {c.n}
              </Box>
            );
          })}
        </Stack>

        <DataTable
          rows={visible}
          loading={loading}
          error={failed}
          onRetry={() => setReloadKey((k) => k + 1)}
          errorTitle="Couldn't load timesheets"
          empty={{
            title: "Nothing here",
            description: "No one matches the current filter for this week.",
          }}
          onRowClick={(r) => (r.sheetId ? openSheet(r) : undefined)}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={columns}
        />

        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "1.125rem" }}>
          Showing {visible.length} of {rows.length} {rows.length === 1 ? "person" : "people"} · {weekLabel(week)}
        </Box>
      </MainBox>
    </Box>
  );
};

export default Timesheet;
